import { Controller } from "@hotwired/stimulus";
import { Board } from "../utils/board";
import { PreviewBoard } from "../utils/preview_board";
import { debounce } from "../utils/helpers";
import { colors } from "../utils/colors";

export default class extends Controller {
  static targets = [
    "shape",
    "board",
    "color",
    "tubeTooltip",
    "modeToggle",
    "shapesMenu",
    "objectMenu",
    "animationMenu",
    "animationPreview",
    "destinationText",
    "orderNumber",
    "mediaControls",
    "playButton",
    "pauseButton",
    "designButton",
    "animateButton",
  ];

  static values = { icon: String, color: String };

  connect() {
    this.cleanUpOldCanvases();
    this.board = new Board(this.boardTarget);
    this.previewBoard = null;
    this.selectedShape = "";
    this.connecting = false;
    this.connectingObjects = [];
    this.dragging = false;
    this.animateMode = false;
    this.editingSideText = false;
    this.board.setup();
    this.board.loadScene();
    this.registerMouseEvents();
    this.initializeDebouncedChange();
  }

  initializeDebouncedChange = () => {
    this.debouncedChange = debounce(() => {
      this.previewBoard.resetAnimation();
      const selectedColorInput = document.querySelector(
        'input[name="cube-color"]:checked',
      );

      const text = this.destinationTextTarget.value;
      const color = selectedColorInput?.value || "white";

      this.previewBoard.currentAnimations[0].destination.text = text;
      this.previewBoard.currentAnimations[0].destination.color = color;
      const animations = this.previewBoard.jsonToTransmissions(
        this.previewBoard.currentAnimations,
      );
      this.previewBoard.play({ animations });
    }, 300);
  };

  change(event) {
    if (this.previewBoard.flowAnimation) return;
    this.debouncedChange(event);
  }

  selectColor() {
    const selectedColorInput = document.querySelector(
      'input[name="cube-color"]:checked',
    );

    const text = this.destinationTextTarget.value;
    const color = selectedColorInput?.value || "white";

    this.previewBoard.currentAnimations[0].destination.text = text;
    this.previewBoard.currentAnimations[0].destination.color = color;

    const animations = this.previewBoard.jsonToTransmissions(
      this.previewBoard.currentAnimations,
    );
    this.previewBoard.play({ animations });
  }

  showTubeTooltip(event) {
    this.tubeTooltipTarget.style.left = `${event.clientX + 10}px`;
    this.tubeTooltipTarget.style.top = `${event.clientY + 10}px`;
    this.tubeTooltipTarget.classList.remove("hidden");
  }

  hideTubeTooltip() {
    this.tubeTooltipTarget.classList.add("hidden");
  }

  registerMouseEvents() {
    this.boardTarget.tabIndex = 0;
    this.boardTarget.addEventListener("mousedown", this.onMouseDown);
    this.boardTarget.addEventListener("mousemove", this.onMouseMove);
    this.boardTarget.addEventListener("mouseup", this.onMouseUp);
    this.boardTarget.addEventListener("dblclick", this.onDoubleClick);
    this.boardTarget.addEventListener("keydown", this.onKeyDown);
  }

  startConnecting = () => {
    this.connecting = true;
  };

  setFontSize = (event) => {
    const value = event.currentTarget.dataset.fontSize;
    this.board.setFontSize(this.board.selectedObject, value);
  };

  onDoubleClick = (event) => {
    const intersect = this.board.getIntersectedObject(event);
    if (intersect) {
      this.board.selectedObject = intersect.object;
      this.board.startEditingText(intersect);
      this.editingSideText = true;
    }
  };

  onKeyDown = (event) => {
    if (
      event.key === "Backspace" &&
      this.board.selectedObject &&
      !this.editingSideText &&
      !this.board.editingLabel
    ) {
      this.deleteObject();
    }
  };

  onMouseDown = (event) => {
    if (this.animateMode) return;

    const intersect = this.board.getIntersectedObject(event);

    if (!intersect) return;

    if (this.board.gizmo.isGizmo(intersect.object)) {
      this.board.gizmo.startDragging(event, intersect.object);
    } else if (intersect.object.name === "__tube__") {
      this.handleTubeClick(intersect);
    } else if (this.connecting) {
      this.handleConnectingStart(event, intersect);
    } else {
      this.handleObjectSelection(intersect);
    }
  };

  onMouseMove = (event) => {
    if (this.animateMode) return;

    if (this.board.gizmo.isDragging) {
      this.boardTarget.style.cursor = "grabbing";
      this.board.gizmo.handleDragging(event);
      this.board.gizmo.update();
      this.board.updateTubesPositions();
    } else if (this.dragging) {
      this.board.updateObjectPosition(event);
    } else if (this.connecting && this.connectingObjects[0]) {
      this.updateConnectingPreview(event);
    } else {
      this.onHover(event);
    }
  };

  onMouseUp = (event) => {
    if (this.animateMode) return;

    const intersect = this.board.getIntersectedObject(event);

    if (this.connecting && intersect) {
      this.handleConnectingEnd(intersect);
    } else if (this.selectedShape === "cube") {
      this.handleShapeInsert(event);
    } else if (!intersect) {
      this.handleClickOnEmptySpace();
    } else if (this.dragging && this.board.selectedObject) {
      this.board.saveCube(this.board.selectedObject);
    }

    this.boardTarget.style.cursor = "default";
    this.editingSideText = false;
    this.dragging = false;
    this.board.gizmo.stopDragging();
  };

  handleTubeClick(intersect) {
    this.board.selectedTube = intersect.object;
    this.openAnimationMenu();
  }

  handleConnectingStart(event, intersect) {
    const startPosition = intersect.object.position;
    const endPosition = this.board.getIntersectPoint(event);
    this.connectingObjects.push(intersect.object);
    this.board.addPreviewTube({ startPosition, endPosition });
  }

  handleObjectSelection(intersect) {
    if (this.board.selectedObject?.id !== intersect.object.id) {
      this.board.deselectObject(this.board.selectedObject);
    }
    this.board.selectedObject = intersect.object;
    this.dragging = true;
    this.board.selectObject(this.board.selectedObject);

    this.toggleObjectMenu({ show: true });
  }

  updateConnectingPreview(event) {
    const startPosition = this.connectingObjects[0].position;
    const endPosition = this.board.getIntersectPoint(event);
    this.board.updatePreviewTube({ startPosition, endPosition });
  }

  onHover(event) {
    const intersect = this.board.getIntersectedObject(event);

    if (intersect) {
      const object = intersect.object;
      if (object.name === "__tube__") {
        this.boardTarget.style.cursor = "pointer";
        this.showTubeTooltip(event, intersect);
      } else if (this.board.gizmo.isGizmo(object)) {
        this.board.gizmo.highlightHandle(object, true);
        this.boardTarget.style.cursor = "grab";
      }
    } else {
      this.boardTarget.style.cursor = "default";
      this.hideTubeTooltip();
      this.board.gizmo.removeHighlight();
    }
  }

  handleConnectingEnd(intersect) {
    this.connectingObjects.push(intersect.object);
    if (this.connectingObjects.length === 2) {
      this.board.removePreviewTube();
      const tube = this.board.connectObjects(
        this.connectingObjects[0],
        this.connectingObjects[1],
      );
      this.board.addAnimation(
        tube,
        this.connectingObjects[0],
        this.connectingObjects[1],
      );
      this.board.saveTube(tube);
      this.connecting = false;
      this.unhighlight();
      this.connectingObjects = [];
    }
  }

  handleShapeInsert(event) {
    const object = this.board.insertShape(this.selectedShape, event);
    this.board.selectObject(object);
    this.toggleObjectMenu({ show: true });
    this.unhighlight();
  }

  handleClickOnEmptySpace() {
    this.toggleObjectMenu({ show: false });
    this.board.deselectObject(this.board.selectedObject);
    this.board.removePreviewTube();
    this.connectingObjects = [];
  }

  async createPreviewBoard(tube) {
    const tubeRecord = await this.board.database.getTubeById(tube.uuid);
    const cubeRecord1 = await this.board.database.getCubeById(
      tubeRecord.firstObjectUuid,
    );
    const cubeRecord2 = await this.board.database.getCubeById(
      tubeRecord.secondObjectUuid,
    );
    const tubeRecords = [tubeRecord];
    const cubeRecords = [cubeRecord1, cubeRecord2];

    const previewBoard = new PreviewBoard(this.animationPreviewTarget);
    previewBoard.loadScene(cubeRecords, tubeRecords);
    previewBoard.setup();
    previewBoard.currentAnimations = tubeRecord.animations;

    return previewBoard;
  }

  stopAnimationPreview() {
    this.cleanUpOldCanvases("animation-preview");
    this.previewBoard?.cleanup();
    this.previewBoard = null;
  }

  async openAnimationMenu() {
    this.board.stopRenderLoop();

    this.toggleObjectMenu({ show: false });
    const animation = this.board.selectedTube.userData.animations[0];

    this.destinationTextTarget.value = animation.destination.text;

    this.orderNumberTarget.value = animation.orderNumber;

    const selectedColor = animation.destination.color;
    const colorRadio = document.querySelector(
      `input[name="cube-color"][value="${selectedColor}"]`,
    );
    if (colorRadio) {
      colorRadio.checked = true;
    }

    this.animationMenuTarget.style.display = "";

    this.previewBoard = await this.createPreviewBoard(this.board.selectedTube);
    const animations = this.previewBoard.jsonToTransmissions(
      this.previewBoard.currentAnimations,
    );
    this.previewBoard.play({ animations });
  }

  closeAnimationMenu() {
    this.stopAnimationPreview();
    this.board.startRenderLoop();

    this.animationMenuTarget.style.display = "none";
  }

  saveAnimationMenu() {
    const selectedColorInput = document.querySelector(
      'input[name="cube-color"]:checked',
    );

    const text = this.destinationTextTarget.value;
    const color = selectedColorInput?.value || "white";
    const orderNumber = this.orderNumberTarget.value || 1;

    this.board.updateAnimation(
      this.board.selectedTube,
      text,
      color,
      orderNumber,
    );
    this.board.saveTube(this.board.selectedTube);

    this.closeAnimationMenu();
  }

  setColor(event) {
    const value = event.currentTarget.dataset.colorValue;
    this.board.setCubeColor(this.board.selectedObject, value);
    this.board.saveCube(this.board.selectedObject);
  }

  toggleObjectMenu({ show } = {}) {
    this.objectMenuTarget.style.display = show ? "" : "none";
  }

  highlight(event) {
    this.shapeTargets.forEach((shape) => {
      shape.style.backgroundColor = "";
      shape.style.transform = "";
    });

    event.currentTarget.style.backgroundColor = colors["indigo300"];
    event.currentTarget.style.transform = "scale(1.1)";

    const icon = event.currentTarget.dataset.boardIconValue;

    this.toggleObjectMenu({ show: false });

    this.selectedShape = icon;
    this.board.deselectObject(this.board.selectedObject);
  }

  unhighlight() {
    this.selectedShape = "";
    this.shapeTargets.forEach((shape) => {
      shape.style.backgroundColor = "";
      shape.style.transform = "";
    });
  }

  design() {
    this.animateMode = false;

    this.modeToggleTarget.setAttribute("data-state", "design");

    const activeIndicator = document.getElementById("active-indicator");
    activeIndicator.style.transform = "translateX(0%)";

    this.designButtonTarget.classList.remove("text-gray-400");
    this.designButtonTarget.classList.add("text-white");

    this.animateButtonTarget.classList.remove("text-white");
    this.animateButtonTarget.classList.add("text-gray-400");

    this.board.resetAnimation({ timeout: 0 });
    this.toggleObjectMenu({ show: false });
    this.board.deselectObject(this.board.selectedObject);
    this.mediaControlsTarget.style.display = "none";
    this.shapesMenuTarget.style.display = "";
  }

  animate() {
    this.animateMode = true;
    this.toggleObjectMenu({ show: false });
    this.toggleButton({ show: "play" });

    this.modeToggleTarget.setAttribute("data-state", "animate");

    const activeIndicator = document.getElementById("active-indicator");
    activeIndicator.style.transform = "translateX(100%)";

    this.animateButtonTarget.classList.remove("text-gray-400");
    this.animateButtonTarget.classList.add("text-white");

    this.designButtonTarget.classList.remove("text-white");
    this.designButtonTarget.classList.add("text-gray-400");

    this.mediaControlsTarget.style.display = "";
    this.shapesMenuTarget.style.display = "none";
  }

  play() {
    this.playButtonTarget.style.display = "none";

    this.pauseButtonTarget.style.display = "";

    this.board.play({
      onComplete: () => {
        this.toggleButton({ show: "play" });
      },
    });
  }

  pause() {
    //TODO: pause animation
  }

  playNext() {
    this.toggleButton({ show: "pause" });

    this.board.playNext({
      onComplete: () => {
        this.toggleButton({ show: "play" });
      },
    });
  }

  toggleButton({ show = "play" } = {}) {
    if (show === "play") {
      this.playButtonTarget.style.display = "";

      this.pauseButtonTarget.style.display = "none";
    } else {
      this.playButtonTarget.style.display = "none";

      this.pauseButtonTarget.style.display = "";
    }
  }

  deleteObject() {
    this.board.deleteCube(this.board.selectedObject);
    this.board.deleteObject(this.board.selectedObject);

    this.objectMenuTarget.style.display = "none";
  }

  resetObject() {
    this.board.resetObject(this.board.selectedObject);
    this.board.saveCube(this.board.selectedObject);
  }

  rotateObject(event) {
    this.board.rotateObject(this.board.selectedObject, event.target.value);
    this.board.saveCube(this.board.selectedObject);
  }

  scaleObject(event) {
    this.board.scaleObject(this.board.selectedObject, event.target.value);
    this.board.saveCube(this.board.selectedObject);
  }

  cleanUpOldCanvases = (elementId = "board-canvas") => {
    const canvas = document.getElementById(elementId);
    while (canvas.firstChild) {
      canvas.removeChild(canvas.firstChild);
    }
  };

  initialize() {
    this.cleanUpOldCanvases();
  }

  disconnect() {
    this.cleanUpOldCanvases();
    this.board.cleanup();
  }
}
